/* eslint-disable react/jsx-no-useless-fragment */
import React from 'react'
import { Avatar, Chip, IconButton, List, ListItem, Tooltip } from '@mui/material'
import { useLang } from '~/hooks/useLang'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import RefreshIcon from '@mui/icons-material/Refresh'
import { type State } from '@hookstate/core'
import { Media } from '../Media'
import type { CommentDataProps } from '~/features/Dispatch/stores/DispatchCommentState'
import { Comment } from '../Comment'
import DownloadIcon from '@mui/icons-material/Download'
import { registerCopyCommentsTimeline } from '../../services'

interface CommentListProps {
  readonly comments: State<readonly CommentDataProps[]>
  readonly sortComments: State<boolean>
  readonly showSortButton: boolean
  readonly handleSortComments: () => void
  readonly refresh: boolean
  readonly isDispatch?: boolean
  readonly isOccurrence?: boolean
  readonly moveScrolltoComment?: boolean
  readonly isEdit?: boolean
  readonly readonly?: boolean
  readonly dispatchCode?: string
  readonly refreshAction: () => void
}

// eslint-disable-next-line max-lines-per-function
export default function CommentList({
  comments,
  sortComments,
  refresh,
  isDispatch,
  isOccurrence,
  moveScrolltoComment,
  isEdit,
  readonly,
  dispatchCode,
  showSortButton,
  handleSortComments,
  refreshAction,
}: CommentListProps) {
  const { translate } = useLang();

  const handleDownloadComments = async () => {
    const commentsArray = comments.get()
    if (!commentsArray.length) return

    await registerCopyCommentsTimeline({occurrenceId: commentsArray[0].occurrenceId})

    const text = commentsArray
      .map((commentItem) => `${commentItem.comment}\n`)
      .join('\n\n')

    const now = new Date()
    const date = now.toLocaleDateString('pt-BR').replace(/\//g, '-')
    const time = `${now.getHours().toString().padStart(2, '0')}h${now
      .getMinutes()
      .toString()
      .padStart(2, '0')}`

    const timestamp = `${date}_${time}`

    const dispatchCodeName = `${dispatchCode}`

    const fileName = `${dispatchCodeName}_comentarios_${timestamp}.txt`

    const blob = new Blob([text], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const downloadLink = document.createElement('a')
    downloadLink.href = url
    downloadLink.download = fileName
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
    URL.revokeObjectURL(url)
  }

  return (
    <>
      {
        refresh ? (
          <>
            {showSortButton &&
              comments.get().length > 1 && (
                <Tooltip
                  title={translate('Sort name', {
                    code: !sortComments.get()
                      ? translate('Order by date latest')
                      : translate('Order by the oldest date'),
                  })}
                  placement="top"
                >
                  <IconButton
                  onClick={() => handleSortComments()}
                    sx={{
                      position: 'absolute',
                      zIndex: 1,
                      marginTop: '-20px',
                      marginRight: '210px',
                      transform: 'scale(0.6)',
                      color: 'white',
                      backgroundColor: '#009688',
                      '&:hover': { backgroundColor: '#00544a' },
                    }}
                  >
                    <SwapVertIcon />
                  </IconButton>
                </Tooltip>
              )
            }
            {
              showSortButton &&
              comments.get().length > 1 && (
                <Tooltip title={translate('Baixar comentários')} placement="top">
                  <IconButton
                    onClick={handleDownloadComments}
                    sx={{
                      position: 'absolute',
                      zIndex: 1,
                      marginTop: '-20px',
                      marginRight: '270px',
                      transform: 'scale(0.6)',
                      color: 'white',
                      backgroundColor: '#009688',
                      '&:hover': { backgroundColor: '#00544a' },
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              )
            }
            <List dense={true} sx={{ width: '100%' }}>
              {comments
                .get()
                .slice()
                .sort((a, b) => {
                  const dateA = new Date(a.createdAt).getTime()
                  const dateB = new Date(b.createdAt).getTime()
                  return sortComments.get()
                    ? dateB - dateA // DESC: mais recente primeiro
                    : dateA - dateB // ASC: mais antigo primeiro
                })
                .map((commentObject) => {
                  const index = comments
                    .get()
                    .findIndex(
                      (commentItem) => commentItem.id === commentObject.id
                    )
                  return (
                    <ListItem
                      sx={{ padding: '5px 8px' }}
                      key={commentObject.id}
                    >
                      {!commentObject.mediaPath && (
                        <>
                          <Comment
                            isEdit={isEdit && !readonly}
                            commentData={comments.nested(index)}
                            isDispatch={isDispatch}
                            isOccurrence={isOccurrence}
                            moveScrolltoComment={moveScrolltoComment}
                          />
                        </>
                      )}
                      {!!commentObject.mediaPath && (
                        <Media commentData={comments.nested(index)} allComments={comments} />
                      )}
                    </ListItem>
                  )
                })}
            </List>
          </>
        ) : (
          <Chip
            sx={{ marginLeft: '0%', marginTop: '50%' }}
            label={translate('Connection failed! Please try again')}
            avatar={
              <Avatar>
                <RefreshIcon />
              </Avatar>
            }
            onClick={() => (refreshAction ? refreshAction() : false)}
          />
        )
      }
    </>
  )
}